import { Box } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { listPartnerNdcPreferenceCountsApi, listPartnerNdcPreferencesApi } from 'api/inventory';
import useRoles from 'common/hooks/useRoles';
import { getGraphQLErrorMessage } from 'common/utils/i18nErrorTranslations';
import { getPartners } from 'store/thunks/partnerThunks';
import PartnerPreferenceTable from './components/PartnerPreferenceTable';
import PartnerPreferenceToolbar from './components/PartnerPreferenceToolbar';
import RemovePartnerPreferenceDialog from './components/RemovePartnerPreferenceDialog';
import SetPartnerNdcPreferenceDialog from './components/SetPartnerNdcPreferenceDialog';
import { DIALOG_MODE, PAGE_SIZE } from './constants';
import { partnerStatusOf } from './utils/formatters';

const EMPTY_ARRAY = [];

const PartnerNdcPreference = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { viewPartnerNdcPreference, managePartnerNdcPreference } = useRoles();

  const currentTenantId = useSelector(({ auth }) => auth.currentTenantId);
  const allPartners = useSelector(({ partner }) => partner[currentTenantId] ?? EMPTY_ARRAY);

  const [search, setSearch] = useState('');
  const [showInactivePartners, setShowInactivePartners] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // The counts query sorts on partner name only, so the single knob is direction.
  const [sortOrder, setSortOrder] = useState('ASC');
  const [page, setPage] = useState(1);

  const [partners, setPartners] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);

  // Children are lazy-loaded per row, so only one partner is expanded at a time:
  // multi-expand would fire a request per open row, and a refetch afterwards
  // makes the page jump around.
  const [expandedPartnerId, setExpandedPartnerId] = useState(null);
  const [childrenByPartnerId, setChildrenByPartnerId] = useState({});
  const [childLoadingId, setChildLoadingId] = useState(null);
  const [childErrorById, setChildErrorById] = useState({});

  const [dialog, setDialog] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null);

  useEffect(() => {
    document.title = 'Partner Preferences - SmartOS';
  }, []);

  // Needed for the partner picker and for the "this partner has none yet" escape
  // hatch on an empty search result. Cached in the store, so this is a no-op on
  // a warm navigation.
  useEffect(() => {
    if (allPartners.length === 0) dispatch(getPartners());
  }, [dispatch, allPartners.length]);

  // Debounced with a timer ref rather than lodash.debounce: the test setup
  // replaces lodash.debounce with a pass-through that has no .cancel(), so a
  // cancel on unmount would throw.
  const searchTimerRef = useRef(null);

  useEffect(() => () => clearTimeout(searchTimerRef.current), []);

  const handleSearchChange = (value) => {
    setSearch(value);
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(value.trim());
      setPage(1);
    }, 300);
  };

  // Partner name only: the backend has no free-text search across drug or NDC, so
  // the toolbar cannot find a partner by the NDC it is pinned to.
  //
  // isActive is pinned to true. The backend flag is a Boolean, so it can ask for
  // active or for inactive but never for both — "show superseded as well" is not
  // expressible. Superseded rows are history the screen does not surface; a
  // preference is only ever replaced, so one active row per partner + product is
  // the whole truth.
  const filter = useMemo(
    () => ({
      partnerName: debouncedSearch || null,
      isActive: true,
      // Server-side now: the backend excludes disabled and suspended partners from
      // both the rows and the count, so pages stay full and the total is exact.
      // Trimming the fetched page here used to leave both wrong.
      includeInactivePartners: showInactivePartners,
    }),
    [debouncedSearch, showInactivePartners]
  );

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setError('');

    listPartnerNdcPreferenceCountsApi({
      ...filter,
      sortOrder,
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    })
      .then((response) => {
        if (cancelled) return;
        setPartners(response?.result ?? []);
        setTotalCount(response?.count ?? 0);
      })
      .catch((apiError) => {
        if (cancelled) return;
        // Inline error with a Retry rather than an empty table: a failed read is
        // indistinguishable from "no preferences configured" otherwise.
        // Deliberately not routed through handleError -- that opens the global
        // error dialog, which would sit on top of the Retry offered right here.
        setError(getGraphQLErrorMessage(apiError));
        setPartners([]);
        setTotalCount(0);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [filter, sortOrder, page, reloadToken]);

  const fetchChildren = useCallback((partnerId) => {
    setChildLoadingId(partnerId);
    setChildErrorById((previous) => ({ ...previous, [partnerId]: '' }));

    listPartnerNdcPreferencesApi({ partnerUuid: partnerId })
      .then((response) => {
        setChildrenByPartnerId((previous) => ({
          ...previous,
          [partnerId]: response?.result ?? [],
        }));
      })
      .catch((apiError) => {
        setChildErrorById((previous) => ({
          ...previous,
          [partnerId]: getGraphQLErrorMessage(apiError),
        }));
      })
      .finally(() => {
        setChildLoadingId((current) => (current === partnerId ? null : current));
      });
  }, []);

  const handleToggleExpand = (partnerId) => {
    if (expandedPartnerId === partnerId) {
      setExpandedPartnerId(null);
      return;
    }
    setExpandedPartnerId(partnerId);
    // Cached from a previous expand -- re-fetching would only make the row flicker.
    if (!childrenByPartnerId[partnerId]) fetchChildren(partnerId);
  };

  // A write changes both the partner's rows and its count, so the child cache is
  // dropped and the parent page refetched. The affected partner is expanded so
  // the result of the write is on screen -- on an add the partner may be entering
  // the list for the first time, and a collapsed new row reads as "nothing
  // happened".
  const handleWriteSuccess = ({ partnerUuid }) => {
    setChildrenByPartnerId((previous) => {
      const next = { ...previous };
      delete next[partnerUuid];
      return next;
    });
    setExpandedPartnerId(partnerUuid);
    fetchChildren(partnerUuid);
    setReloadToken((token) => token + 1);
  };

  // The counts query returns no partner status, so it is joined from the partner
  // list already fetched for the picker — no extra request.
  const partnerStatusById = useMemo(() => {
    const byId = {};
    allPartners.forEach((item) => {
      const status = partnerStatusOf(item);
      if (status) byId[item.partnerUuid] = status;
    });
    return byId;
  }, [allPartners]);

  // The list only holds partners that already have a preference, so a search for
  // a partner with none returns nothing. Match the term against the cached
  // partner list to offer that partner directly instead of a dead end.
  const searchMatchedPartner = useMemo(() => {
    if (!debouncedSearch) return null;
    const term = debouncedSearch.toLowerCase();
    // Only an active partner: the dialog's picker offers active partners only, so
    // offering a disabled one here would open a dialog that cannot select it.
    return (
      allPartners.find(
        (item) =>
          (item.partnerName ?? '').toLowerCase().includes(term) &&
          item.enabled !== false &&
          !item.isSuspended
      ) ?? null
    );
  }, [debouncedSearch, allPartners]);

  if (!viewPartnerNdcPreference) return null;

  return (
    <Box sx={{ px: 3, pt: 2, pb: 3 }}>
      <PartnerPreferenceToolbar
        search={search}
        onSearchChange={handleSearchChange}
        showInactivePartners={showInactivePartners}
        onShowInactivePartnersChange={(next) => {
          setShowInactivePartners(next);
          setPage(1);
        }}
        canManage={managePartnerNdcPreference}
        onAdd={() => setDialog({ mode: DIALOG_MODE.ADD_ANY, partner: null, preference: null })}
      />

      <PartnerPreferenceTable
        partners={partners}
        totalCount={totalCount}
        loading={loading}
        error={error}
        onRetry={() => setReloadToken((token) => token + 1)}
        page={page}
        onPageChange={setPage}
        sortOrder={sortOrder}
        onSortOrderChange={(next) => {
          setSortOrder(next);
          setPage(1);
        }}
        expandedPartnerId={expandedPartnerId}
        onToggleExpand={handleToggleExpand}
        childrenByPartnerId={childrenByPartnerId}
        childLoadingId={childLoadingId}
        childErrorById={childErrorById}
        onChildRetry={fetchChildren}
        canManage={managePartnerNdcPreference}
        hasActiveSearch={Boolean(debouncedSearch)}
        searchMatchedPartner={searchMatchedPartner}
        onAddForSearchedPartner={(matched) =>
          setDialog({
            mode: DIALOG_MODE.ADD_FOR_PARTNER,
            partner: { partnerId: matched.partnerUuid, partnerName: matched.partnerName },
            preference: null,
          })
        }
        onAdd={() => setDialog({ mode: DIALOG_MODE.ADD_ANY, partner: null, preference: null })}
        onAddForPartner={(partner) =>
          setDialog({ mode: DIALOG_MODE.ADD_FOR_PARTNER, partner, preference: null })
        }
        onChange={(partner, preference) =>
          setDialog({ mode: DIALOG_MODE.CHANGE, partner, preference })
        }
        onRemove={(partner, preference) => setRemoveTarget({ partner, preference })}
        onCopyPartnerId={(partner) =>
          enqueueSnackbar(`Partner ID copied: ${partner.partnerId}`, { variant: 'success' })
        }
        partnerStatusById={partnerStatusById}
      />

      <SetPartnerNdcPreferenceDialog
        open={Boolean(dialog)}
        mode={dialog?.mode ?? DIALOG_MODE.ADD_ANY}
        partner={dialog?.partner ?? null}
        preference={dialog?.preference ?? null}
        preferencesByPartnerId={childrenByPartnerId}
        onNeedPartnerPreferences={fetchChildren}
        onClose={() => setDialog(null)}
        onSaved={handleWriteSuccess}
      />

      <RemovePartnerPreferenceDialog
        open={Boolean(removeTarget)}
        partner={removeTarget?.partner ?? null}
        preference={removeTarget?.preference ?? null}
        onClose={() => setRemoveTarget(null)}
        onRemoved={handleWriteSuccess}
      />
    </Box>
  );
};

export default PartnerNdcPreference;
